import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { Form } from 'core/components';
import { AccessPermissions } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { FormProvider, useFormContext } from 'core/states';
import { IPartFinderReel } from 'partFinder/types';
import {
  CustomerQuoteUpsertForm,
  useAlternateDefaultValues,
} from 'quotes/components/CustomerQuoteUpsert';
import { QuoteEntityType } from 'quotes/enums';

import { ReelLinesTable } from './ReelLinesTable';
import { getSchema } from './schema';
import { IAddReelsToNewQuoteForm, IAddReelsToQuoteForm } from './types';
import { getCreateQuoteDefaultValues, getDefaultLines } from './utils';

interface IProps {
  reels: IPartFinderReel[];
  entityType: QuoteEntityType;
  isCreatingQuote: boolean;
  onLinesCountChange: (count: number) => void;
}

// The lines table on its own for an existing quote, or below the create-quote fields when the
// shopper is creating one - one form either way, so one submit validates both.
export const ReelLinesForm = forwardRef(function ReelLinesForm(
  { reels, entityType, isCreatingQuote, onLinesCountChange }: IProps,
  ref
) {
  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const omitDefaultSalesLocation = hasPermission(
    AccessPermissions.QUOTE_OMIT_DEFAULT_SALES_LOCATION
  );

  const alternateDefaultValues = useAlternateDefaultValues({
    entityType,
    isAlternateVersionEnabled,
  });

  const defaultValues = useMemo(
    () => ({
      ...(isCreatingQuote
        ? getCreateQuoteDefaultValues({
            user,
            entityType,
            isAlternateVersionEnabled,
            omitDefaultSalesLocation,
            alternateDefaultValues,
          })
        : {}),
      lines: getDefaultLines(reels),
    }),
    [
      alternateDefaultValues,
      entityType,
      isAlternateVersionEnabled,
      isCreatingQuote,
      omitDefaultSalesLocation,
      reels,
      user,
    ]
  );

  const schema = useMemo(
    () =>
      getSchema({
        isCreatingQuote,
        entityType,
        companySettings: user?.companySettings,
        isAlternateVersionEnabled,
      }),
    [entityType, isAlternateVersionEnabled, isCreatingQuote, user?.companySettings]
  );

  return (
    <FormProvider schema={schema} defaultValues={defaultValues}>
      <ReelLinesFormFields
        ref={ref}
        reels={reels}
        entityType={entityType}
        isCreatingQuote={isCreatingQuote}
        defaultValues={defaultValues}
        onLinesCountChange={onLinesCountChange}
      />
    </FormProvider>
  );
});

interface IFormFieldsProps extends IProps {
  defaultValues: IAddReelsToQuoteForm;
}

const ReelLinesFormFields = forwardRef(function ReelLinesFormFields(
  { reels, entityType, isCreatingQuote, defaultValues, onLinesCountChange }: IFormFieldsProps,
  ref
) {
  const { user } = useAuthUser();
  const { trigger, getValues } = useFormContext();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  useImperativeHandle(
    ref,
    () => ({
      triggerFormSubmit: () =>
        new Promise((resolve, reject) => {
          trigger()
            .then((isValid) => (isValid ? resolve(getValues()) : reject()))
            .catch(reject);
        }),
    }),
    [getValues, trigger]
  );

  const linesTable = <ReelLinesTable reels={reels} onLinesCountChange={onLinesCountChange} />;

  if (!isCreatingQuote) {
    return (
      <Form isOpen withoutButtons inline withoutNotification>
        {linesTable}
      </Form>
    );
  }

  return (
    <CustomerQuoteUpsertForm<IAddReelsToNewQuoteForm>
      isOpen
      isAlternateVersionEnabled={isAlternateVersionEnabled}
      onClose={(reset: UseFormReset<IAddReelsToNewQuoteForm>) =>
        reset(defaultValues as IAddReelsToNewQuoteForm)
      }
      user={user}
      defaultValues={defaultValues as IAddReelsToNewQuoteForm}
      entityType={entityType}
      extraFields={linesTable}
      withoutForm
      withoutNotification
    />
  );
});
